// Dashboard — phase 1: balance card (equity / cash / PnL / positions / sparkline).
//
// The sparkline is a hand‑rolled SVG over `events[].equity_krw` so we avoid
// pulling in a charting dep. Positions render as an admin-design Table.

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  api,
  type CryptoVerdict,
  type DashboardBalance,
  type DashboardBithumb,
  type DashboardIndices,
  type DashboardNews,
  type IntradayPayload,
  type DashboardRetrospective,
  type JournalEntry,
  type MarketOverview,
  type MarketOverviewItem,
  type NewsSummary,
  type NewsSummaryItem,
  type NewsSentiment,
  type QuoteSnapshot,
  type StockSuggestion,
  type SuggestionsGenerateResponse,
  type SuggestionsListResponse,
  type WatchlistEntry,
} from '@/api/client'
import type { TradingPlan } from '@/api/types'
import { Badge, Button, Card, Input, StatCard } from '@/admin-design/_common'
import { formatRelativeKST, formatTimeShortKST } from '@/lib/utils'
import { krxName, symbolName } from '@/lib/krx-names'

export default function Dashboard() {
  const balance = useQuery<DashboardBalance>({
    queryKey: ['dashboard', 'balance'],
    queryFn: () => api.dashboardBalance(120),
    refetchInterval: 15_000,
  })
  const news = useQuery<DashboardNews>({
    queryKey: ['dashboard', 'news'],
    queryFn: () => api.dashboardNews({ limit: 40, hours: 6 }),
    // Summary is served from cache only (non-blocking). When the
    // server flags summary_pending, the LLM is working on it in the
    // background — poll every 10s until it lands, then fall back to
    // the 2-minute steady-state interval. staleTime stays at 2m so
    // route-changes don't fire redundant requests.
    staleTime: 2 * 60_000,
    refetchInterval: (q) => {
      const d = q.state.data as DashboardNews | undefined
      if (d?.summary_pending && !d.summary) return 10_000
      return 2 * 60_000
    },
    refetchOnWindowFocus: false,
  })
  const retro = useQuery<DashboardRetrospective>({
    queryKey: ['dashboard', 'retrospective'],
    queryFn: () => api.dashboardRetrospective(),
    refetchInterval: 60_000,
  })
  const nextPlan = useQuery<TradingPlan>({
    queryKey: ['dashboard', 'next-day-plan'],
    queryFn: () => api.dashboardNextDayPlan(),
    refetchInterval: 5 * 60_000,
    retry: false, // 404 is expected on fresh installs
  })

  const indices = useQuery<DashboardIndices>({
    queryKey: ['dashboard', 'indices'],
    queryFn: () => api.dashboardIndices(),
    // Server caches the payload for 60s (indicesCacheTTL). Mirror that
    // on the client so tab-switches reuse the cached value instantly
    // instead of waiting for a network round-trip to the (cached)
    // endpoint. Background refetch still runs every 60s.
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
  })

  return (
    <div className="space-y-6">
      {/* Health strip — first thing the operator sees. Only renders when
          something is actually wrong; no noise on the happy path. */}
      <KISHealthStrip />

      {/* ═══════════════════════════════════════════════════════════
          시장 흐름 + 뉴스 — 증권 (왼쪽) · 암호화폐 (오른쪽)
          ═══════════════════════════════════════════════════════════ */}
      <FlowOverviewSection
        news={news.data}
        newsIsPending={news.isPending}
        newsError={news.error}
      />

      {/* ═══════════════════════════════════════════════════════════
          증권사 · KIS 한국 주식
          KRX 지표 strip + LIVE/PAPER 뱃지를 이 그룹 안쪽으로 이동 —
          페이지 최상단에 있으면 증권사 컨텍스트인지 불분명해서 조직
          단위로 묶음. 뱃지는 header 우측, 지표 strip은 본문 첫 행.
          ═══════════════════════════════════════════════════════════ */}
      <ProviderSection
        title="증권사 · KIS 한국 주식"
        subtitle="KOSPI / KOSDAQ 6자리 티커 기반. 장중 시간대만 실시간 데이터."
        tone="kis"
        headerExtras={
          balance.data && (
            <Badge variant={balance.data.trade_mode === 'live' ? 'destructive' : 'muted'}>
              {balance.data.trade_mode === 'live' ? 'LIVE 실전' : 'PAPER 모의'}
            </Badge>
          )
        }
      >
        <KRXIndicesStrip indices={indices.data} />
        <BalanceSection data={balance.data} isPending={balance.isPending} error={balance.error} />
        <WatchedSection />
        <IntradayKISSection />
        <SuggestionsSection />
      </ProviderSection>

      {/* ═══════════════════════════════════════════════════════════
          암호화폐 · Bithumb
          ═══════════════════════════════════════════════════════════ */}
      <ProviderSection
        title="암호화폐 · Bithumb"
        subtitle="KRW 마켓 24/7. 실계좌 직결 — 모든 주문은 가드레일 통과 후 실행."
        tone="crypto"
      >
        {/* CryptoMarketOverviewSection folded into the top-of-page
            FlowCard's 3rd tab (가격 신호) — see FlowCard. Card stays
            focused on provider state (cash / positions / shadow
            verdicts journal). */}
        <BithumbSection />
      </ProviderSection>

      {/* 공통 저널 / 계획 — 증권사 세션 한정 기능이지만 "공통" 그룹으로
          분리해서 상위 두 섹션이 시각적으로 엉키지 않도록 배치. */}
      <ProviderSection title="공통 · 일지 & 계획" tone="neutral">
        <RetrospectiveSection data={retro.data} isPending={retro.isPending} error={retro.error} />
        <NextDayPlanSection plan={nextPlan.data} isPending={nextPlan.isPending} error={nextPlan.error} />
      </ProviderSection>
    </div>
  )
}

// FlowOverviewSection is the top-of-page shared surface. Two columns,
// each a tabbed card bundling 흐름(sentiment graph) + 뉴스(summary
// grid) for one asset class. KIS on the left with the primary accent,
// 암호화폐 on the right with the amber accent — matches the
// ProviderSection tones used below so the visual language stays
// consistent with the detail split. Flow and news are kept together
// in one column because they feed the same market-read: the graph
// gives "where we're going", the news grid gives "why".
// KISHealthStrip surfaces the paused state of the market + trade KIS
// clients. Invisible when everything's fine (no visual noise on happy
// path); banner + resume button when a scope is paused. The resume
// button just clears the flag — if upstream is still actually down,
// the breaker will trip again on the next call, but at least the
// operator can kick off a recovery attempt without shelling into the DB.
function KISHealthStrip() {
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['kis', 'broker-state'],
    queryFn: () => api.kisBrokerState(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  })
  const resume = useMutation({
    mutationFn: (name: 'kis_market' | 'kis_trade') => api.kisBrokerResume(name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kis', 'broker-state'] })
      // Also refetch surfaces that depend on KIS — indices, balance,
      // quotes — so they retry immediately instead of waiting the next
      // refetchInterval.
      qc.invalidateQueries({ queryKey: ['dashboard', 'indices'] })
      qc.invalidateQueries({ queryKey: ['dashboard', 'balance'] })
    },
  })
  const paused = (q.data?.items ?? []).filter((r) => r.paused)
  if (paused.length === 0) return null
  return (
    <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm dark:border-red-900 dark:bg-red-950">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-red-700 dark:text-red-300">
            KIS 일부 기능 정지 중 ({paused.length}건)
          </p>
          <p className="mt-0.5 text-xs text-red-600/80 dark:text-red-400/80">
            scope별 서킷 브레이커가 trip 됐습니다. 원인 확인 후 "정지 해제"
            누르면 다음 호출부터 재시도합니다.
          </p>
        </div>
      </div>
      <ul className="space-y-2">
        {paused.map((row) => (
          <li
            key={row.name}
            className="flex items-start justify-between gap-3 rounded border border-red-200 bg-white/80 p-2 text-xs dark:border-red-900 dark:bg-red-950/50"
          >
            <div className="min-w-0 flex-1">
              <p className="font-mono font-semibold text-red-700 dark:text-red-300">
                {row.name}
                <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-normal text-red-700 dark:bg-red-900 dark:text-red-300">
                  fails: {row.consecutive_failures}
                </span>
              </p>
              {row.reason && (
                <p className="mt-1 break-all font-mono text-[11px] text-red-600/70 dark:text-red-400/70">
                  {row.reason}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() =>
                resume.mutate(row.name as 'kis_market' | 'kis_trade')
              }
              disabled={resume.isPending}
              className="rounded border border-red-300 bg-white px-2 py-1 text-[11px] font-medium text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-700 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800"
            >
              정지 해제
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

function FlowOverviewSection({
  news,
  newsIsPending,
  newsError,
}: {
  news: DashboardNews | undefined
  newsIsPending: boolean
  newsError: Error | null
}) {
  return (
    <section className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <FlowCard
        title="증권사 · KIS Flow"
        scope="market"
        accent="border-l-4 border-l-primary"
        news={news}
        newsIsPending={newsIsPending}
        newsError={newsError}
      />
      <FlowCard
        title="암호화폐 · Crypto Flow"
        scope="crypto"
        accent="border-l-4 border-l-amber-400"
        news={news}
        newsIsPending={newsIsPending}
        newsError={newsError}
      />
    </section>
  )
}

// FlowCard is one column of the FlowOverviewSection: a tabbed surface
// with 흐름(sentiment-over-time sparkline + distribution) and
// 뉴스(scope-filtered summary-card grid). Same component serves both
// KIS and 암호화폐 sides via scope prop. Both tabs consume data the
// dashboard already has loaded — flow hits /market-overview?scope=…
// and news filters the shared /dashboard/news payload — so switching
// tabs is instant, no extra roundtrip.
function FlowCard({
  title,
  scope,
  accent,
  news,
  newsIsPending,
  newsError,
}: {
  title: string
  scope: 'market' | 'crypto'
  accent: string
  news: DashboardNews | undefined
  newsIsPending: boolean
  newsError: Error | null
}) {
  // Crypto side exposes a third tab — 가격 신호 — that reads
  // shadow-session verdicts instead of news sentiment. Used to live
  // in a separate CryptoMarketOverviewSection card; folded in here
  // so the operator sees both flavours of "market feel" side by side
  // without another scroll: 뉴스 감성 vs 가격 기반 판정.
  // KIS side keeps the original 2-tab shape (no shadow session).
  type Tab = 'flow' | 'verdicts' | 'news'
  const tabs: { key: Tab; label: string }[] =
    scope === 'crypto'
      ? [
          { key: 'flow', label: '뉴스 감성' },
          { key: 'verdicts', label: '가격 신호' },
          { key: 'news', label: '뉴스' },
        ]
      : [
          { key: 'flow', label: '흐름' },
          { key: 'news', label: '뉴스' },
        ]
  const [tab, setTab] = useState<Tab>('flow')
  return (
    <Card className={accent}>
      <Card.Body>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
          <div className="flex gap-0.5 rounded-md border border-border bg-card p-0.5">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`rounded px-3 py-1 text-xs font-medium ${
                  tab === t.key
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted/50'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        {tab === 'flow' ? (
          <FlowTab scope={scope} />
        ) : tab === 'verdicts' ? (
          <CryptoVerdictsOverviewTab />
        ) : (
          <NewsSection
            data={news}
            isPending={newsIsPending}
            error={newsError}
            scope={scope}
          />
        )}
      </Card.Body>
    </Card>
  )
}

// KRXIndicesStrip renders KOSPI / KOSDAQ as a slim market-context strip
// at the top of the dashboard. Replaces the old AccountOverviewSection
// which duplicated the KIS equity/cash/pnl figures already shown by
// BalanceSection inside the "증권사 · KIS 한국 주식" ProviderSection.
// Keeps the index readings visible (they live on /dashboard/indices,
// not on /balance) without re-showing account numbers the operator
// already sees below.
function KRXIndicesStrip({
  indices,
}: {
  indices: DashboardIndices | undefined
}) {
  const hasItems = indices && indices.items.length > 0
  const statusText = (() => {
    if (!indices) return 'KIS 미연결 — Settings → KIS 에서 엔드포인트/토큰을 확인하세요.'
    if (hasItems) return null
    if (indices.error) return `KIS 지수 조회 실패 — ${indices.error}`
    return 'KIS 지수 데이터 수신 대기 중'
  })()

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-md border border-border bg-muted/20 px-3 py-2 text-sm">
      <span className="text-xs font-medium text-muted-foreground">
        KRX 지표
      </span>
      {hasItems ? (
        <>
          {indices!.items.map((ix) => {
            const ageSec = Math.max(0, Math.floor(Date.now() / 1000) - ix.at)
            const hasChange =
              typeof ix.change === 'number' && typeof ix.change_pct === 'number'
            const tone =
              !hasChange || ix.change === 0
                ? 'text-muted-foreground'
                : (ix.change as number) > 0
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-red-600 dark:text-red-400'
            return (
              <span
                key={ix.code}
                className={`inline-flex items-baseline gap-1.5 ${
                  ix.stale ? 'opacity-60' : ''
                }`}
                title={
                  ix.stale
                    ? `KIS 조회 실패 — 마지막 수신값 (${fmtAgeKR(ageSec)} 전)`
                    : undefined
                }
              >
                <span className="text-xs text-muted-foreground">{ix.label}</span>
                <span className="font-mono text-sm font-semibold text-foreground">
                  {new Intl.NumberFormat('ko-KR', {
                    maximumFractionDigits: 2,
                  }).format(ix.value)}
                </span>
                {hasChange && (
                  <span className={`font-mono text-xs font-medium ${tone}`}>
                    {(ix.change as number) >= 0 ? '+' : ''}
                    {new Intl.NumberFormat('ko-KR', {
                      maximumFractionDigits: 2,
                    }).format(ix.change as number)}
                    <span className="ml-1">
                      ({(ix.change_pct as number) >= 0 ? '+' : ''}
                      {(ix.change_pct as number).toFixed(2)}%)
                    </span>
                  </span>
                )}
                {ix.stale && (
                  <span className="rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                    {fmtAgeKR(ageSec)} 전
                  </span>
                )}
              </span>
            )
          })}
          {indices!.error && (
            <span className="text-[10px] text-muted-foreground">
              (일부 조회 실패: {indices!.error})
            </span>
          )}
        </>
      ) : (
        <span className="text-xs text-muted-foreground">{statusText}</span>
      )}
    </div>
  )
}

function BalanceSection({
  data,
  isPending,
  error,
}: {
  data: DashboardBalance | undefined
  isPending: boolean
  error: Error | null
}) {
  if (isPending) {
    return (
      <Card>
        <Card.Body>
          <p className="text-sm text-muted-foreground">Loading balance…</p>
        </Card.Body>
      </Card>
    )
  }
  if (error) {
    return (
      <Card>
        <Card.Body>
          <p className="text-sm text-red-600">Failed to load balance: {String(error)}</p>
        </Card.Body>
      </Card>
    )
  }
  if (!data) return null

  const deltaTone: 'positive' | 'negative' | 'neutral' =
    data.delta_vs_prev_close > 0 ? 'positive' : data.delta_vs_prev_close < 0 ? 'negative' : 'neutral'

  return (
    <div className="space-y-3">
      {data.broker_error && (
        <Card className="border-warning-soft bg-warning-soft">
          <Card.Body className="text-xs text-warning-soft-foreground">
            <p className="font-medium">KIS 잔고를 불러오지 못했습니다 — 모든 수치가 0으로 표시됩니다.</p>
            <p className="mt-1 font-mono break-all">{data.broker_error}</p>
            <p className="mt-1 opacity-80">
              Settings → KIS 패널에서 엔드포인트(paper/live) · 토큰 · 계좌번호를 확인하세요.
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Compact single-card balance row. Equity / Cash / Realised /
          Unrealised + a small delta-vs-prev-close footer. The full
          sparkline card was removed per the layout pass — the delta
          line already tells the operator whether the day is green
          or red, and the equity history lives on the Positions page
          for when a deeper view is needed. */}
      <Card>
        <Card.Body>
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <h2 className="text-sm font-semibold text-muted-foreground">
              잔고
            </h2>
            <span
              className={
                deltaTone === 'positive'
                  ? 'text-xs font-medium text-emerald-600'
                  : deltaTone === 'negative'
                  ? 'text-xs font-medium text-red-600'
                  : 'text-xs font-medium text-muted-foreground'
              }
            >
              전일 대비 {fmtSignedKRW(data.delta_vs_prev_close)} ({fmtPct(data.delta_vs_prev_pct)})
              {data.trading_date && (
                <span className="ml-2 text-muted-foreground">· {data.trading_date}</span>
              )}
            </span>
          </div>
          <section className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <StatCard label="Equity" value={fmtKRW(data.equity_krw)} />
            <StatCard label="Cash" value={fmtKRW(data.cash_krw)} />
            <StatCard
              label="Realised P&L"
              value={fmtSignedKRW(data.realised_pnl_today)}
              tone={statCardTone(data.realised_pnl_today)}
            />
            <StatCard
              label="Unrealised P&L"
              value={fmtSignedKRW(data.unrealised_pnl)}
              tone={statCardTone(data.unrealised_pnl)}
            />
          </section>
        </Card.Body>
      </Card>

      {data.positions.length > 0 && (
        <Card>
          <Card.Body>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-muted-foreground">
                보유 ({data.positions.length})
              </h2>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-1">Ticker</th>
                  <th className="py-1 text-right">Qty</th>
                  <th className="py-1 text-right">Avg</th>
                  <th className="py-1 text-right">Mkt</th>
                  <th className="py-1 text-right">Value</th>
                  <th className="py-1 text-right">P&L</th>
                </tr>
              </thead>
              <tbody>
                {data.positions.map((p) => {
                  const unreal = (p.mkt_price - p.avg_cost) * p.qty
                  return (
                    <tr key={p.ticker} className="border-t border-border">
                      <td className="py-1">
                        <span className="font-mono">{p.ticker}</span>
                        {krxName(p.ticker) && (
                          <span className="ml-1.5 text-xs text-muted-foreground">
                            {krxName(p.ticker)}
                          </span>
                        )}
                      </td>
                      <td className="py-1 text-right">{p.qty.toLocaleString()}</td>
                      <td className="py-1 text-right">{fmtKRW(p.avg_cost)}</td>
                      <td className="py-1 text-right">{fmtKRW(p.mkt_price)}</td>
                      <td className="py-1 text-right">{fmtKRW(p.mkt_value)}</td>
                      <td
                        className={
                          'py-1 text-right ' +
                          (unreal > 0
                            ? 'text-emerald-600'
                            : unreal < 0
                            ? 'text-red-600'
                            : '')
                        }
                      >
                        {fmtSignedKRW(unreal)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

// (EquitySparkline removed — the equity-vs-prev-close sparkline card
// was dropped in the layout pass; the delta% in BalanceSection's
// header line still carries the day's signal.)

function fmtKRW(n: number): string {
  if (!Number.isFinite(n)) return '—'
  return `₩${Math.round(n).toLocaleString()}`
}

function fmtSignedKRW(n: number): string {
  if (!Number.isFinite(n) || n === 0) return '₩0'
  const sign = n > 0 ? '+' : '−'
  return `${sign}₩${Math.abs(Math.round(n)).toLocaleString()}`
}

function fmtPct(n: number): string {
  if (!Number.isFinite(n) || n === 0) return '0.00%'
  const sign = n > 0 ? '+' : ''
  return `${sign}${n.toFixed(2)}%`
}

// statCardTone maps a signed number to the admin-design StatCard palette.
// Zero gets "muted" so a quiet day doesn't look alarming. We avoid the info
// tone to keep the palette to success/destructive for P&L semantics.
function statCardTone(n: number): 'success' | 'destructive' | 'muted' {
  if (n > 0) return 'success'
  if (n < 0) return 'destructive'
  return 'muted'
}

// ----- News -----------------------------------------------------------------

// (KISFlowCard removed — the flow graph moved to the top-level
// FlowOverviewSection for side-by-side KIS+Crypto comparison; the
// news tab content is now rendered directly as <NewsSection scope="market"/>
// inside the KIS ProviderSection.)

// FlowTab is the graph-centric view: per-hour sentiment averages
// derived from the summary items the dashboard already fetches.
// Single SVG sparkline (no chart deps) + the existing distribution
// bar / score gauge below for the snapshot cross-check. Parameterised
// by scope so the same component serves both KIS 흐름(scope=market)
// and 암호화폐 흐름(scope=crypto) cards.
function FlowTab({ scope }: { scope: 'market' | 'crypto' }) {
  // Operator-selectable flow window. Default 7d is a good balance —
  // short enough that the sparkline has visible day-to-day slope when
  // only a week of summaries exists, but long enough to smooth out a
  // single noisy day. 3d for a zoomed-in "this week" view, 30d for
  // the month retention ceiling. All three hit /market-overview with
  // a different hours param + cached query key so switching is instant
  // once fetched.
  const [windowDays, setWindowDays] = useState<3 | 7 | 30>(7)
  const q = useQuery<MarketOverview>({
    queryKey: ['dashboard', 'market-overview', scope, windowDays],
    queryFn: () => api.dashboardMarketOverview(windowDays * 24, 50, scope),
    refetchInterval: 2 * 60_000,
    refetchOnWindowFocus: false,
  })
  // Hooks must run on every render in the same order — compute
  // `buckets` BEFORE any conditional early-return. React error #310
  // ("Rendered more hooks than during the previous render") fires
  // when a loading→data transition flips the hook count.
  //
  // Server-side flow_points wins — pre-aggregated across the FULL
  // 30-day rowset (up to 2000). Client-side bucketByDay only sees the
  // `items` display slice (50 rows), so when the latest 50 summaries
  // landed in one calendar day the sparkline collapsed to a single
  // point. Fall back to client-side bucketing for servers that pre-date
  // the flow_points field so the dashboard stays useful during rollout.
  const items = q.data?.items ?? []
  const flowPoints = q.data?.flow_points
  const buckets = useMemo(() => {
    if (flowPoints && flowPoints.length > 0) {
      return flowPoints.map((p) => ({
        hour: p.day_kst,
        mean: p.mean,
        count: p.count,
      }))
    }
    return bucketByDay(items, windowDays)
  }, [flowPoints, items, windowDays])

  if (!q.data) {
    return <p className="text-sm text-muted-foreground">Loading…</p>
  }
  const d = q.data
  const scopeLabel = scope === 'crypto' ? '암호화폐' : '시장'

  return (
    <div className="space-y-3">
      <div>
        <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span>최근 {windowDays}일 {scopeLabel} 감성 흐름 (일별 평균)</span>
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5 rounded border border-border bg-card p-0.5">
              {([3, 7, 30] as const).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setWindowDays(n)}
                  className={`rounded px-2 py-0.5 text-[10px] font-medium ${
                    windowDays === n
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted/50'
                  }`}
                >
                  {n}일
                </button>
              ))}
            </div>
            <span className="font-mono">
              평균 score{' '}
              <span
                className={
                  d.sentiment_score > 0.2
                    ? 'text-emerald-500'
                    : d.sentiment_score < -0.2
                    ? 'text-red-500'
                    : 'text-foreground'
                }
              >
                {d.sentiment_score >= 0 ? '+' : ''}
                {d.sentiment_score.toFixed(2)}
              </span>
            </span>
          </div>
        </div>
        <SentimentSparkline buckets={buckets} />
      </div>
      <MarketSentimentChart
        bullish={d.bullish}
        bearish={d.bearish}
        neutral={d.neutral}
        mixed={d.mixed}
        score={d.sentiment_score}
      />
      <p className="text-[11px] text-muted-foreground">
        총 {d.item_count}건 · 최근 {windowDays}일 scope={scope} 요약본 집계.
        개별 기사는 우상단 "뉴스" 탭.
      </p>
    </div>
  )
}

// SentimentSparkline draws a hand-rolled SVG line chart of mean
// sentiment per hour bucket. Centred on 0 with red/green gradient
// fill + per-point bubble sized by count. Pure SVG so no charting
// dependency; the chart is small (140-220 px tall) so it doesn't
// dominate the dashboard.
function SentimentSparkline({
  buckets,
}: {
  buckets: Array<{ hour: number; mean: number; count: number }>
}) {
  if (buckets.length === 0) {
    return (
      <div className="rounded-md border border-border bg-card p-4 text-center text-xs text-muted-foreground">
        감성 데이터가 없습니다. LLM 요약본이 쌓이면 여기에 흐름이 그려집니다.
      </div>
    )
  }
  const w = 600
  const h = 140
  const padX = 24
  const padY = 16
  const innerW = w - padX * 2
  const innerH = h - padY * 2
  const xFor = (i: number) =>
    padX + (buckets.length === 1 ? innerW / 2 : (i / (buckets.length - 1)) * innerW)
  const yFor = (v: number) => padY + ((1 - v) / 2) * innerH
  const points = buckets.map((b, i) => `${xFor(i)},${yFor(b.mean)}`).join(' ')
  const areaPoints =
    `${xFor(0)},${yFor(0)} ` +
    points +
    ` ${xFor(buckets.length - 1)},${yFor(0)}`
  const maxCount = Math.max(1, ...buckets.map((b) => b.count))

  return (
    <div className="rounded-md border border-border bg-card p-2">
      <svg viewBox={`0 0 ${w} ${h}`} className="h-32 w-full">
        <defs>
          <linearGradient id="sentGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
            <stop offset="50%" stopColor="#94a3b8" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        {/* Centre baseline (score=0). */}
        <line
          x1={padX}
          x2={w - padX}
          y1={yFor(0)}
          y2={yFor(0)}
          stroke="currentColor"
          strokeOpacity="0.2"
          strokeDasharray="4 4"
        />
        {/* Filled gradient under the line. */}
        <polygon points={areaPoints} fill="url(#sentGrad)" />
        {/* Sentiment line. */}
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* Bubbles per bucket — radius scaled by count, colour by sentiment. */}
        {buckets.map((b, i) => {
          const r = 2 + (b.count / maxCount) * 4
          const fill =
            b.mean > 0.2 ? '#10b981' : b.mean < -0.2 ? '#ef4444' : '#94a3b8'
          return (
            <circle
              key={b.hour}
              cx={xFor(i)}
              cy={yFor(b.mean)}
              r={r}
              fill={fill}
            >
              <title>
                {`${b.hour}시 · 평균 ${b.mean >= 0 ? '+' : ''}${b.mean.toFixed(
                  2,
                )} · ${b.count}건`}
              </title>
            </circle>
          )
        })}
        {/* Axis labels. */}
        <text x={padX} y={h - 2} fontSize="9" fill="currentColor" opacity="0.6">
          {buckets[0]?.hour}시
        </text>
        <text
          x={w - padX}
          y={h - 2}
          fontSize="9"
          textAnchor="end"
          fill="currentColor"
          opacity="0.6"
        >
          {buckets[buckets.length - 1]?.hour}시
        </text>
      </svg>
    </div>
  )
}

// bucketByDay groups summary items into per-day buckets over the last
// `windowDays` (KST). Returns one point per calendar day in chronological
// order (oldest → newest), so the SentimentSparkline reads left-to-right
// as a time series. Empty days are collapsed (skipped) rather than
// rendered as zero so the line doesn't spike through 0 on quiet days.
//
// SentimentSparkline was built for the old hour-of-day shape with
// `hour` as the x-axis key. We reuse the same point struct — its
// `hour` field carries the day-epoch (seconds / 86400) so X-axis
// ordering works without touching the chart component.
function bucketByDay(
  items: MarketOverviewItem[],
  windowDays: number,
): Array<{ hour: number; mean: number; count: number }> {
  if (!items || items.length === 0) return []
  const nowSec = Math.floor(Date.now() / 1000)
  const cutoff = nowSec - windowDays * 86400
  const acc = new Map<number, { sum: number; count: number; day: number }>()
  for (const it of items) {
    if (!it.published_at || it.published_at < cutoff) continue
    // Day-bucket in KST (UTC+9) — integer day index since epoch.
    const kstSec = it.published_at + 9 * 3600
    const day = Math.floor(kstSec / 86400)
    const e = acc.get(day) ?? { sum: 0, count: 0, day }
    e.sum += it.sentiment_score
    e.count += 1
    acc.set(day, e)
  }
  return Array.from(acc.values())
    .map((e) => ({ hour: e.day, mean: e.sum / e.count, count: e.count }))
    .sort((a, b) => a.hour - b.hour)
}

// (KISNewsSection removed — KISFlowCard now hosts the news content
// inside its "뉴스" tab via direct NewsSection call.)

// (CryptoFlowCard removed — flow moved to the top-level
// FlowOverviewSection; crypto news now renders via
// <NewsSection scope="crypto"/> inside the Bithumb ProviderSection.)

// ProviderSection is the grouping wrapper for dashboard sub-cards,
// rendered with a tone-coded header band so the two asset classes
// (KIS stocks / Bithumb crypto) never visually blur together.
function ProviderSection({
  title,
  subtitle,
  tone,
  headerExtras,
  children,
}: {
  title: string
  subtitle?: string
  tone: 'kis' | 'crypto' | 'neutral'
  /** Right-aligned header slot — used by the KIS section to pin the
   *  LIVE/PAPER mode badge next to the title (where the old top-of-
   *  page mode badge used to live). */
  headerExtras?: React.ReactNode
  children: React.ReactNode
}) {
  const accent =
    tone === 'kis'
      ? 'border-l-4 border-l-primary'
      : tone === 'crypto'
      ? 'border-l-4 border-l-amber-400'
      : 'border-l-4 border-l-border'
  return (
    <section className={`rounded-lg border border-border bg-muted/20 pl-1 ${accent}`}>
      <header className="flex items-start justify-between gap-3 px-4 pt-3 pb-2">
        <div>
          <h1 className="text-base font-semibold text-foreground">{title}</h1>
          {subtitle && (
            <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
          )}
        </div>
        {headerExtras && <div className="shrink-0">{headerExtras}</div>}
      </header>
      <div className="space-y-3 px-2 pb-3">{children}</div>
    </section>
  )
}

// NewsSection renders the summarised-news browser inside a FlowCard's
// "뉴스" tab. `scope` controls which subset of data.summary shows:
//   - 'market'  → stock-side (everything that isn't crypto)
//   - 'crypto'  → only scope=crypto items (암호화폐 흐름 카드)
// Layout is the 2-column grid NewsSummaryBrowser already applies, so
// both KIS and crypto cards get the same card-grid UI.
// NewsSection renders the summarised-news browser. No Card wrapper —
// consumer (FlowCard's 뉴스 tab) already provides the card chrome,
// so this just emits the content directly to avoid nested cards.
function NewsSection({
  data,
  isPending,
  error,
  scope,
}: {
  data: DashboardNews | undefined
  isPending: boolean
  error: Error | null
  scope: 'market' | 'crypto'
}) {
  if (isPending) {
    return <p className="text-sm text-muted-foreground">Loading news…</p>
  }
  if (error) {
    return <p className="text-sm text-red-600">Failed to load news: {String(error)}</p>
  }
  if (!data) return null

  // Scope-filtered summary, mirroring the server-side CryptoPredicateSQL:
  //   origin IN (rss_crypto, rss_x_crypto) → always crypto
  //   origin == rss_ai                     → never crypto
  //   otherwise → scope column decides
  // This guards against the LLM mis-scoping an rss_ai item as crypto
  // (operator-reported failure mode).
  const isCryptoItem = (it: NewsSummaryItem) => {
    const origin = it.origin ?? ''
    if (origin === 'rss_crypto' || origin === 'rss_x_crypto') return true
    if (origin === 'rss_ai') return false
    return it.scope === 'crypto'
  }
  const summary = data.summary
    ? {
        ...data.summary,
        items: data.summary.items.filter((it) =>
          scope === 'crypto' ? isCryptoItem(it) : !isCryptoItem(it),
        ),
      }
    : undefined
  const totalRaw =
    scope === 'crypto'
      ? (data.crypto?.length ?? 0)
      : data.holdings.length + data.market_wide.length
  const status = data.summary_status
  const inflightElapsed = status?.inflight_started_at
    ? Math.max(0, Math.floor((Date.now() - new Date(status.inflight_started_at).getTime()) / 1000))
    : null
  return (
    <div className="space-y-3">
      <SummaryHeader
        summary={summary}
        pending={data.summary_pending}
        elapsedSec={inflightElapsed}
      />
      {summary && summary.items.length > 0 ? (
        <NewsSummaryBrowser
          items={summary.items}
          heldTickers={data.held_tickers}
        />
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">
          {data.summary_pending
            ? `뉴스 요약 워커 실행 중${
                inflightElapsed !== null ? ` (${inflightElapsed}초 경과)` : ''
              }…`
            : totalRaw === 0
            ? '수집된 뉴스가 없습니다.'
            : '다음 워커 틱에서 요약이 생성됩니다.'}
        </p>
      )}
    </div>
  )
}

// NewsSummaryBrowser is the sentiment-tabbed + searchable + paginated
// summary view. Replaces the flat "render everything newest-first"
// list — at 30+ summaries the old rendering dumped a wall of cards
// with no way to focus on bullish vs bearish or to find a specific
// headline. Ten items per page keeps the card compact; a "더 보기"
// / "이전" pair paginates locally (no server round-trip).
function NewsSummaryBrowser({
  items,
  heldTickers,
}: {
  items: NewsSummaryItem[]
  heldTickers: string[]
}) {
  const [sentiment, setSentiment] = useState<'all' | NewsSentiment>('all')
  const [needle, setNeedle] = useState('')
  const [page, setPage] = useState(0)
  const pageSize = 10

  // Counts drive the tab badges — computed from the full item set so
  // the operator sees how much moves to each bucket even before
  // selecting a tab.
  const counts = useMemo(() => {
    const c: Record<'all' | NewsSentiment, number> = {
      all: items.length,
      bullish: 0,
      bearish: 0,
      neutral: 0,
      mixed: 0,
    }
    for (const it of items) c[it.sentiment] = (c[it.sentiment] ?? 0) + 1
    return c
  }, [items])

  const filtered = useMemo(() => {
    const n = needle.trim().toLowerCase()
    return items.filter((it) => {
      if (sentiment !== 'all' && it.sentiment !== sentiment) return false
      if (!n) return true
      return (
        it.title.toLowerCase().includes(n) ||
        (it.summary_ko ?? '').toLowerCase().includes(n) ||
        (it.ticker ?? '').toLowerCase().includes(n) ||
        (it.origin ?? '').toLowerCase().includes(n)
      )
    })
  }, [items, sentiment, needle])

  // Reset pagination whenever the filter set shifts so the operator
  // doesn't land on a hidden page 3 of an empty result.
  const prevFilterKey = useMemo(
    () => `${sentiment}|${needle}`,
    [sentiment, needle],
  )
  const [filterKey, setFilterKey] = useState(prevFilterKey)
  if (filterKey !== prevFilterKey) {
    setFilterKey(prevFilterKey)
    setPage(0)
  }

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage = Math.min(page, totalPages - 1)
  const pageItems = filtered.slice(
    safePage * pageSize,
    safePage * pageSize + pageSize,
  )

  return (
    <div className="mt-3 space-y-3">
      <SentimentTabBar current={sentiment} counts={counts} onChange={setSentiment} />
      <div className="flex items-center gap-2">
        <Input
          type="text"
          placeholder="요약·제목·티커 검색"
          value={needle}
          onChange={(e) => setNeedle(e.target.value)}
          className="flex-1"
        />
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {filtered.length}건 · {safePage + 1}/{totalPages}
        </span>
      </div>
      {pageItems.length === 0 ? (
        <p className="text-sm text-muted-foreground">표시할 요약이 없습니다.</p>
      ) : (
        // Responsive 2-col grid on md+, single col on small screens.
        // grid-flow-row + auto-rows-fr makes sibling cards equalise
        // height when the summary lines clamp at 2.
        <ul className="grid grid-cols-1 gap-2 md:grid-cols-2 auto-rows-fr">
          {pageItems.map((it) => (
            <li key={it.id}>
              <SummaryItemCard item={it} heldTickers={heldTickers} />
            </li>
          ))}
        </ul>
      )}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            size="sm"
            variant="secondary"
            disabled={safePage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ← 이전
          </Button>
          <span className="text-xs text-muted-foreground">
            {safePage * pageSize + 1}–
            {Math.min(filtered.length, (safePage + 1) * pageSize)} / {filtered.length}
          </span>
          <Button
            size="sm"
            variant="secondary"
            disabled={safePage >= totalPages - 1}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          >
            다음 →
          </Button>
        </div>
      )}
    </div>
  )
}

function SentimentTabBar({
  current,
  counts,
  onChange,
}: {
  current: 'all' | NewsSentiment
  counts: Record<'all' | NewsSentiment, number>
  onChange: (v: 'all' | NewsSentiment) => void
}) {
  const chips: Array<{ key: 'all' | NewsSentiment; label: string; tone: string }> = [
    { key: 'all',     label: '전체', tone: 'border-border' },
    { key: 'bullish', label: '강세', tone: 'border-emerald-400' },
    { key: 'bearish', label: '약세', tone: 'border-red-400' },
    { key: 'neutral', label: '중립', tone: 'border-border' },
    { key: 'mixed',   label: '혼조', tone: 'border-amber-400' },
  ]
  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((c) => {
        const n = counts[c.key] ?? 0
        const active = current === c.key
        const disabled = n === 0 && c.key !== 'all'
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => onChange(c.key)}
            disabled={disabled}
            className={
              active
                ? `rounded-full border-2 ${c.tone} bg-primary px-3 py-1 text-xs font-medium text-primary-foreground`
                : `rounded-full border-2 ${c.tone} bg-card px-3 py-1 text-xs font-medium text-foreground hover:bg-muted/50 disabled:opacity-40`
            }
          >
            {c.label} <span className="ml-1 opacity-70">{n}</span>
          </button>
        )
      })}
    </div>
  )
}

function SummaryHeader({
  summary,
  pending,
  elapsedSec,
}: {
  summary?: NewsSummary
  pending: boolean
  elapsedSec: number | null
}) {
  if (!summary) {
    return (
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-muted-foreground">News</h2>
        {pending && (
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <span
              aria-hidden="true"
              className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary/60"
            />
            요약 생성 중{elapsedSec !== null ? ` · ${elapsedSec}s` : ''}
          </span>
        )}
      </div>
    )
  }
  const created = new Date(summary.created_at)
  const ageMin = Math.max(0, Math.floor((Date.now() - created.getTime()) / 60000))
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold text-muted-foreground">News</h2>
        <SentimentBadge sentiment={summary.sentiment} />
      </div>
      <span className="text-xs text-muted-foreground inline-flex items-center gap-2">
        score {summary.sentiment_score.toFixed(2)} · {summary.input_count}건 · {ageMin}분 전
        {pending && (
          <span className="inline-flex items-center gap-1">
            <span
              aria-hidden="true"
              className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary/60"
            />
            갱신 중
          </span>
        )}
      </span>
    </div>
  )
}

function SentimentBadge({ sentiment }: { sentiment: NewsSentiment }) {
  const label: Record<NewsSentiment, string> = {
    bullish: '강세',
    bearish: '약세',
    neutral: '중립',
    mixed:   '혼조',
  }
  const variant: Record<NewsSentiment, 'success' | 'destructive' | 'muted' | 'warning'> = {
    bullish: 'success',
    bearish: 'destructive',
    neutral: 'muted',
    mixed:   'warning',
  }
  return (
    <Badge variant={variant[sentiment]} shape="pill">
      {label[sentiment]}
    </Badge>
  )
}

// SummaryItemCard renders one per-article card: Korean summary as the
// headline, original title + origin + time meta below, and the WHOLE
// card is a click target that opens the article URL in a new tab.
// Held-ticker items get a subtle accent so they stand out without the
// old Holdings / Market-wide section split.
function SummaryItemCard({
  item,
  heldTickers,
}: {
  item: NewsSummaryItem
  heldTickers: string[]
}) {
  const held = item.ticker && heldTickers.includes(item.ticker)
  const tone: Record<NewsSentiment, string> = {
    bullish: 'border-l-emerald-400',
    bearish: 'border-l-red-400',
    neutral: 'border-l-muted',
    mixed:   'border-l-amber-400',
  }
  // Compact grid-cell layout: tighter padding, 2-line clamp on the
  // summary so rows line up in a 2-col grid, meta row collapsed to
  // a single line with overflow ellipsis.
  const body = (
    <div
      className={`h-full rounded-md border border-border bg-card px-2.5 py-2 border-l-4 ${tone[item.sentiment]} ${
        held ? 'ring-1 ring-primary/20' : ''
      } ${item.url ? 'hover:shadow-sm transition-shadow' : ''}`}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <SentimentBadge sentiment={item.sentiment} />
          {held && (
            <Badge variant="info" shape="pill">
              보유
            </Badge>
          )}
          {item.ticker && (
            <span className="text-[11px] text-muted-foreground">
              <span className="font-mono">{item.ticker}</span>
              {krxName(item.ticker) && (
                <span className="ml-1">{krxName(item.ticker)}</span>
              )}
            </span>
          )}
        </div>
        <span className="whitespace-nowrap text-[11px] text-muted-foreground">
          {item.published_at ? fmtTimeAgo(item.published_at) : ''}
        </span>
      </div>
      <p className="line-clamp-2 text-[13px] leading-snug">{item.summary_ko}</p>
      <p
        className="mt-1 truncate text-[11px] text-muted-foreground"
        title={item.title}
      >
        {item.origin && <span className="mr-1">{item.origin}</span>}
        <span className="opacity-70">· {item.title}</span>
        {item.url && <span aria-hidden="true" className="ml-1">↗</span>}
      </p>
    </div>
  )
  if (!item.url) return body
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block h-full"
      title="클릭해서 원본 기사 열기"
    >
      {body}
    </a>
  )
}

// fmtTimeAgo delegates to the shared formatRelativeKST so dashboard
// and /news surfaces render identical relative labels ("1시간 21분 전"
// vs the old "1h ago" split). 24h+ falls through to absolute KST.
function fmtTimeAgo(epochSec: number): string {
  if (!epochSec) return ''
  return formatRelativeKST(epochSec)
}

// fmtAgeKR turns an elapsed seconds count into a compact 한국어
// "Nm" / "Nh Mm" / "Nd" form. Used by the stale-value badges on the
// indices strip and watched quote chips.
function fmtAgeKR(sec: number): string {
  if (sec < 60) return `${sec}초`
  const m = Math.floor(sec / 60)
  if (m < 60) return `${m}분`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}시간 ${m % 60}분`
  const d = Math.floor(h / 24)
  return `${d}일`
}

// ----- Retrospective / 거래일지 ---------------------------------------------

function RetrospectiveSection({
  data,
  isPending,
  error,
}: {
  data: DashboardRetrospective | undefined
  isPending: boolean
  error: Error | null
}) {
  if (isPending) {
    return (
      <Card>
        <Card.Body>
          <p className="text-sm text-muted-foreground">Loading journal…</p>
        </Card.Body>
      </Card>
    )
  }
  if (error) {
    return (
      <Card>
        <Card.Body>
          <p className="text-sm text-red-600">Failed to load journal: {String(error)}</p>
        </Card.Body>
      </Card>
    )
  }
  if (!data) return null

  const { summary, entries } = data

  return (
    <Card>
      <Card.Body>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-muted-foreground">
              Trading journal · 거래일지
            </h2>
            <p className="text-xs text-muted-foreground">
              {summary.date} · {summary.trade_mode || '—'}
            </p>
          </div>
          <div className="flex gap-2 text-xs">
            <span className="rounded bg-muted px-2 py-0.5">
              {summary.total_trades} trades
            </span>
            <span className="rounded bg-emerald-100 px-2 py-0.5 text-emerald-700">
              {summary.wins}W
            </span>
            <span className="rounded bg-red-100 px-2 py-0.5 text-red-700">
              {summary.losses}L
            </span>
            {summary.open_carried > 0 && (
              <span className="rounded bg-amber-100 px-2 py-0.5 text-amber-700">
                {summary.open_carried} open
              </span>
            )}
            {summary.guardrail_events > 0 && (
              <span className="rounded bg-purple-100 px-2 py-0.5 text-purple-700">
                {summary.guardrail_events} guard
              </span>
            )}
          </div>
        </div>

        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            오늘은 체결된 거래가 없습니다.
          </p>
        ) : (
          <ul className="space-y-2">
            {entries.map((e, idx) => (
              <JournalRow key={`${e.ticker}-${e.entry_at}-${idx}`} entry={e} />
            ))}
          </ul>
        )}
      </Card.Body>
    </Card>
  )
}

function JournalRow({ entry }: { entry: JournalEntry }) {
  const tone =
    entry.realised_pnl > 0
      ? 'text-emerald-600'
      : entry.realised_pnl < 0
      ? 'text-red-600'
      : 'text-muted-foreground'
  const kindLabel: Record<JournalEntry['kind'], string> = {
    closed: '청산',
    open: '보유',
    scale_in: '추가매수',
    scale_out: '부분청산',
  }
  const kindVariant: Record<
    JournalEntry['kind'],
    'muted' | 'info' | 'warning' | 'success'
  > = {
    closed: 'success',
    open: 'warning',
    scale_in: 'info',
    scale_out: 'info',
  }
  return (
    <li className="rounded-md border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm">{entry.ticker}</span>
            {krxName(entry.ticker) && (
              <span className="text-sm text-muted-foreground">
                {krxName(entry.ticker)}
              </span>
            )}
            <Badge variant={kindVariant[entry.kind]} shape="pill">
              {kindLabel[entry.kind]}
            </Badge>
            {entry.plan_match && (
              <Badge variant="info" shape="pill">
                plan
              </Badge>
            )}
            <span className="text-xs text-muted-foreground">
              {entry.qty.toLocaleString()} shares
            </span>
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {entry.entry_at && <>entry {fmtTimeShort(entry.entry_at)} · </>}
            {entry.exit_at ? <>exit {fmtTimeShort(entry.exit_at)} · </> : null}
            {entry.entry_price ? <>₩{Math.round(entry.entry_price).toLocaleString()}</> : null}
            {entry.exit_price ? <> → ₩{Math.round(entry.exit_price).toLocaleString()}</> : null}
          </div>
          {(entry.entry_reason || entry.exit_reason) && (
            <div className="mt-1.5 text-xs text-muted-foreground">
              {entry.entry_reason && <div>진입: {entry.entry_reason}</div>}
              {entry.exit_reason && <div>청산: {entry.exit_reason}</div>}
            </div>
          )}
        </div>
        <div className={`text-right text-sm font-medium ${tone}`}>
          {entry.kind === 'open' ? (
            <span className="text-muted-foreground">holding</span>
          ) : (
            <>
              <div>{fmtSignedKRW(entry.realised_pnl)}</div>
              <div className="text-xs">{fmtPct(entry.realised_pnl_pct)}</div>
            </>
          )}
        </div>
      </div>
    </li>
  )
}

// fmtTimeShort is a thin alias over formatTimeShortKST — every time the
// journal renders must be in KST so an operator traveling abroad doesn't
// see entry/exit times slip by their local UTC offset.
const fmtTimeShort = formatTimeShortKST

// ----- Next-day plan --------------------------------------------------------

function NextDayPlanSection({
  plan,
  isPending,
  error,
}: {
  plan: TradingPlan | undefined
  isPending: boolean
  error: Error | null
}) {
  if (isPending) {
    return (
      <Card>
        <Card.Body>
          <p className="text-sm text-muted-foreground">Loading next-day plan…</p>
        </Card.Body>
      </Card>
    )
  }
  if (error || !plan) {
    return (
      <Card>
        <Card.Body>
          <h2 className="text-sm font-semibold text-muted-foreground mb-1">
            Next trading day plan
          </h2>
          <p className="text-sm text-muted-foreground">
            아직 다음 거래일 계획이 없습니다. 밤 분석 작업이 실행되면 표시됩니다.
          </p>
        </Card.Body>
      </Card>
    )
  }

  const regimeVariant: Record<string, 'success' | 'warning' | 'destructive' | 'muted' | 'info'> = {
    risk_on: 'success',
    risk_off: 'destructive',
    neutral: 'muted',
    degraded: 'warning',
  }
  const regimeKR: Record<string, string> = {
    risk_on: '위험 선호',
    risk_off: '위험 회피',
    neutral: '중립',
    degraded: '분석 실패',
  }
  const variant = regimeVariant[plan.regime] ?? 'info'
  const regimeLabel = regimeKR[plan.regime] ?? plan.regime

  return (
    <Card>
      <Card.Body>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-muted-foreground">
              Next trading day plan
            </h2>
            <p className="text-xs text-muted-foreground">{plan.for_date}</p>
          </div>
          <Badge variant={variant} shape="pill" title={plan.regime}>
            {regimeLabel}
          </Badge>
        </div>

        {plan.macro_summary && (
          <section className="mb-3">
            <h3 className="text-xs font-semibold text-muted-foreground mb-1">
              Macro summary
            </h3>
            <p className="text-sm whitespace-pre-wrap">{plan.macro_summary}</p>
          </section>
        )}

        <section className="mb-3">
          <h3 className="text-xs font-semibold text-muted-foreground mb-1">
            Watchlist ({plan.watchlist.length})
          </h3>
          {plan.watchlist.length === 0 ? (
            <p className="text-sm text-muted-foreground">비어 있습니다.</p>
          ) : (
            <ul className="space-y-1.5">
              {plan.watchlist.map((t) => (
                <li key={t.symbol} className="rounded-md border border-border p-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">{t.symbol}</span>
                    {(t.name || krxName(t.symbol)) && (
                      <span className="text-sm text-muted-foreground">
                        {t.name || krxName(t.symbol)}
                      </span>
                    )}
                    <Badge
                      variant={t.side === 'long' ? 'success' : 'destructive'}
                      shape="pill"
                      title={t.side}
                    >
                      {t.side === 'long' ? '매수' : '매도'}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      conviction {t.conviction?.toFixed(2) ?? '—'}
                    </span>
                  </div>
                  {t.thesis && (
                    <p className="mt-1 text-xs text-muted-foreground">{t.thesis}</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {plan.risk_notes && (
          <section>
            <h3 className="text-xs font-semibold text-muted-foreground mb-1">
              Risk notes
            </h3>
            <p className="text-sm whitespace-pre-wrap">{plan.risk_notes}</p>
          </section>
        )}
      </Card.Body>
    </Card>
  )
}

// ----- Watched ticker -------------------------------------------------------
//
// Live KIS quote for a single symbol. Default 005930 (Samsung Electronics)
// because it's the "is KIS actually reachable right now?" canary — the
// most liquid KRX name with the longest trading session per day.
// Operators type any 6-digit KRX code to track another ticker; the card
// polls every 10 seconds so intraday moves are visible without manual
// refresh.
// WatchedSection renders the persisted monitoring list as a horizontal
// grid of mini-quote cells — every watched ticker shows its current
// price + change% side-by-side so the operator scans the whole
// watchlist at a glance instead of clicking a chip to drill in. Each
// cell fires its own 10s-polling useQuery (React Query dedupes per
// queryKey, so cells of the same ticker share the fetch). Layout is
// a responsive grid: up to 6 per row on xl, collapses gracefully down
// to 2 on mobile.
function WatchedSection() {
  const qc = useQueryClient()
  const list = useQuery<{ items: WatchlistEntry[] }>({
    queryKey: ['watchlist'],
    queryFn: () => api.watchlistList(),
    refetchInterval: 30_000,
  })
  const items = list.data?.items ?? []

  const [draft, setDraft] = useState('')
  const add = useMutation({
    mutationFn: (t: string) => api.watchlistAdd(t),
    onSuccess: (res) => {
      qc.setQueryData(['watchlist'], res)
      setDraft('')
    },
  })
  const remove = useMutation({
    mutationFn: (t: string) => api.watchlistRemove(t),
    onSuccess: (res) => qc.setQueryData(['watchlist'], res),
  })
  // (이름 채우기 button removed per operator — the add/accept flows
  // populate labels inline, so the manual sweep was redundant.)

  return (
    <Card>
      <Card.Body>
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-muted-foreground">
              Watched · KIS quotes
            </h2>
            <p className="text-xs text-muted-foreground">
              모니터링 중 {items.length}건 · 10초마다 자동 갱신 ·
              추천 수락 시 자동 추가
            </p>
          </div>
          <div className="flex items-center gap-2">
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                if (/^\d{6}$/.test(draft)) add.mutate(draft)
              }}
            >
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                placeholder="6자리"
                value={draft}
                onChange={(e) => setDraft(e.target.value.trim())}
                className="w-20 rounded-md border border-border bg-card px-2 py-1 text-sm font-mono"
                aria-label="Ticker to add"
              />
              <button
                type="submit"
                className="rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-foreground hover:bg-muted/50 disabled:opacity-50"
                disabled={!/^\d{6}$/.test(draft) || add.isPending}
              >
                + 추가
              </button>
            </form>
          </div>
        </div>

        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            모니터링 중인 종목이 없습니다. 위에서 6자리 티커를 추가하거나,
            아래 추천 섹션에서 종목을 수락하세요.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {items.map((it) => (
              <WatchedQuoteCell
                key={it.ticker}
                entry={it}
                onRemove={() => {
                  const sourceHint = it.source.startsWith('suggestion:')
                    ? `추천 #${it.source.slice('suggestion:'.length)}`
                    : it.source
                  if (confirm(`${it.ticker} 을/를 모니터링에서 제거할까요? (${sourceHint})`)) {
                    remove.mutate(it.ticker)
                  }
                }}
              />
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// WatchedQuoteCell is one cell in the horizontal watchlist grid —
// ticker + 한글 종목명 on the header row, current price + signed
// change% beneath. The cell colours its price green/red based on
// change direction so the operator can scan for movers visually
// before reading any number. Remove (×) button lives inside the
// cell so every entry is actionable without opening a detail drawer.
function WatchedQuoteCell({
  entry,
  onRemove,
}: {
  entry: WatchlistEntry
  onRemove: () => void
}) {
  const quote = useQuery<QuoteSnapshot>({
    queryKey: ['quote', entry.ticker],
    queryFn: () => api.quote(entry.ticker),
    enabled: /^\d{6}$/.test(entry.ticker),
    refetchInterval: 10_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
  const name = entry.label || krxName(entry.ticker) || ''
  const q = quote.data
  const tone =
    q && q.change_pct > 0.05
      ? 'text-emerald-600'
      : q && q.change_pct < -0.05
      ? 'text-red-600'
      : 'text-foreground'
  const ageSec = q ? Math.max(0, Math.floor(Date.now() / 1000) - q.fetched_at) : 0
  return (
    <div
      className={`group relative flex flex-col gap-1 rounded-md border border-border bg-card p-2 hover:border-primary/50 ${
        q?.stale ? 'opacity-60' : ''
      }`}
      title={
        q?.stale
          ? `KIS 조회 실패 — 마지막 수신값 (${fmtAgeKR(ageSec)} 전)`
          : undefined
      }
    >
      <button
        type="button"
        onClick={onRemove}
        className="absolute right-1 top-1 rounded-full px-1 text-xs text-muted-foreground opacity-0 hover:bg-muted group-hover:opacity-100"
        aria-label={`${entry.ticker} 제거`}
      >
        ×
      </button>
      <div className="flex items-baseline gap-1.5 pr-4">
        <span className="font-mono text-[11px] text-muted-foreground">
          {entry.ticker}
        </span>
        {name && (
          <span className="truncate text-xs font-medium text-foreground">
            {name}
          </span>
        )}
      </div>
      {quote.isPending ? (
        <div className="text-xs text-muted-foreground">…</div>
      ) : quote.error ? (
        <div className="text-xs text-red-600">err</div>
      ) : q ? (
        <>
          <div className={`font-mono text-sm font-semibold ${tone}`}>
            {fmtKRW(q.price)}
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className={`font-mono text-[11px] ${tone}`}>
              {q.change_pct >= 0 ? '+' : ''}
              {q.change_pct.toFixed(2)}%
            </span>
            {q.stale && (
              <span className="rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                {fmtAgeKR(ageSec)} 전
              </span>
            )}
          </div>
        </>
      ) : (
        <div className="text-xs text-muted-foreground">—</div>
      )}
    </div>
  )
}

// ----- Suggestions ----------------------------------------------------------
//
// Advisory LLM-generated stock picks with rationale + evidence. The
// operator reviews each card and either accepts (→ will execute
// manually) or rejects. No auto-trading here — order placement stays
// behind the session orchestrator + guardrails.
// SuggestionsDiagnosticsBanner explains the result of the latest
// "새로 추천" run in plain Korean — most importantly, it differentiates
// "the LLM honestly didn't have anything to recommend" from "the LLM
// did emit suggestions but they all failed validation (non-KRX ticker /
// no evidence / unknown action)". Without this the operator stares at
// an empty list and can't tell which case they're in.
function SuggestionsDiagnosticsBanner({
  stats,
  persisted,
}: {
  stats: SuggestionsGenerateResponse
  persisted: number
}) {
  // Only ticker format failures remain as hard rejects — action
  // drift now falls back to "watch" silently (the suggestion still
  // lands in the review queue). Evidence missing is a downgrade,
  // not a reject.
  const tickerRejects = stats.rejected_non_krx_ticker
  const actionFallbacks = stats.rejected_unknown_action
  const noEvidenceCount = stats.rejected_no_evidence
  const watchedRejects = stats.rejected_already_watched ?? 0
  // allDropped now only trips when every row had a bad ticker —
  // the one case where "LLM said something completely unusable"
  // is actually true.
  const allDropped = stats.llm_row_count > 0 && persisted === 0

  const tone = allDropped
    ? 'border-destructive-soft bg-destructive-soft text-destructive-soft-foreground'
    : tickerRejects > 0 || actionFallbacks > 0 || noEvidenceCount > 0 || watchedRejects > 0
    ? 'border-warning-soft bg-warning-soft text-warning-soft-foreground'
    : 'border-border bg-muted/40 text-muted-foreground'

  return (
    <div className={`mb-3 rounded-md border p-2 text-xs ${tone}`}>
      <div className="font-medium">
        LLM 응답: {stats.llm_row_count}건 · 저장됨 {persisted}건
        {tickerRejects > 0 && ` · 티커오류 ${tickerRejects}건`}
        {actionFallbacks > 0 && ` · action 폴백 ${actionFallbacks}건`}
        {noEvidenceCount > 0 && ` · 근거없음 ${noEvidenceCount}건`}
        {watchedRejects > 0 && ` · 이미 감시중 ${watchedRejects}건`}
      </div>
      {(tickerRejects > 0 || actionFallbacks > 0 || noEvidenceCount > 0 || watchedRejects > 0) && (
        <ul className="mt-1 ml-4 list-disc space-y-0.5">
          {tickerRejects > 0 && (
            <li>
              티커 형식 오류 (6자리 KRX 아님): {tickerRejects}건 — 폐기
              {stats.rejected_ticker_samples && stats.rejected_ticker_samples.length > 0 && (
                <span className="ml-1 font-mono opacity-70">
                  (예: {stats.rejected_ticker_samples.map((s) => `"${s}"`).join(', ')})
                </span>
              )}
            </li>
          )}
          {actionFallbacks > 0 && (
            <li>
              비표준 action: {actionFallbacks}건 — "watch"로 처리되어 저장됨.
              운영자가 수락 시 감시 목록에 추가됩니다.
              {stats.rejected_action_samples && stats.rejected_action_samples.length > 0 && (
                <span className="ml-1 font-mono opacity-70">
                  (예: {stats.rejected_action_samples.map((s) => `"${s}"`).join(', ')})
                </span>
              )}
            </li>
          )}
          {noEvidenceCount > 0 && (
            <li>
              근거(evidence) 누락: {noEvidenceCount}건 — 저장되지만 conviction
              이 0.4 로 제한됩니다. 응답 잘림 가능성.
            </li>
          )}
          {watchedRejects > 0 && (
            <li>
              이미 감시중인 종목: {watchedRejects}건 — 중복 추천 방지 위해 폐기.
              {stats.rejected_watched_samples && stats.rejected_watched_samples.length > 0 && (
                <span className="ml-1 font-mono opacity-70">
                  ({stats.rejected_watched_samples.join(', ')})
                </span>
              )}
            </li>
          )}
        </ul>
      )}
      {allDropped && (
        <p className="mt-1">
          LLM이 추천을 생성했지만 모든 티커 형식이 잘못되었습니다.
          프롬프트/모델 확인 필요.
        </p>
      )}
      {stats.llm_row_count === 0 && (
        <p>
          LLM이 추천할 종목이 없다고 판단했습니다. 뉴스 요약본이 비어있거나
          보유 포지션과 무관한 뉴스만 있는 경우 발생.
        </p>
      )}
    </div>
  )
}

// IntradayKISSection renders the KRX intraday cross-check card. A cron
// every 30 min during trading hours asks a light LLM prompt for the
// current regime + top-3 watchlist picks. Operator uses this to
// pressure-test the nightly plan mid-day. Card shows the latest
// snapshot plus a compact history strip of the prior 12 ticks'
// regime transitions.
function IntradayKISSection() {
  const qc = useQueryClient()
  const q = useQuery<IntradayPayload>({
    queryKey: ['dashboard', 'intraday'],
    queryFn: () => api.dashboardIntraday(20),
    refetchInterval: 2 * 60_000,
    refetchOnWindowFocus: false,
  })
  const items = q.data?.items ?? []
  const latest = items[0]
  const runIntraday = useMutation({
    // force=true also bypasses the trading-hours gate so manual
    // debugging works after-hours. In-hours force is harmless —
    // just equivalent to a regular trigger.
    mutationFn: (force: boolean) => api.intradayRunNow({ force }),
    onSuccess: () => {
      // Intraday prompts are smaller (1200 tokens max); ~30-45s end
      // to end is typical. Invalidate after 15s so the new snapshot
      // lands in the card.
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['dashboard', 'intraday'] })
      }, 15_000)
    },
    onError: (err) => {
      // eslint-disable-next-line no-alert -- surface backend error
      alert(`실시간 분석 실행 실패: ${String(err)}`)
    },
  })
  const runButton = (label: string) => (
    <Button
      size="sm"
      variant="secondary"
      disabled={runIntraday.isPending}
      loading={runIntraday.isPending}
      onClick={() => {
        // Always force=true from the button — the operator clicking
        // means "I want results now" regardless of session state.
        // The analysis is read-only (no orders) so the cost is just
        // one LLM call and one KIS index fetch.
        runIntraday.mutate(true)
      }}
      title="지금 실시간 증시 분석 1회 실행 (LLM 호출 ~30-45s)"
    >
      {label}
    </Button>
  )
  if (q.isPending && !latest) {
    return (
      <Card>
        <Card.Body>
          <p className="text-sm text-muted-foreground">Loading intraday…</p>
        </Card.Body>
      </Card>
    )
  }
  if (!latest) {
    return (
      <Card>
        <Card.Body>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-foreground">
                실시간 증시 분석
              </h2>
              <p className="mt-1 text-xs text-muted-foreground">
                장 중(09:00~15:30 KST)에 30분마다 LLM이 지수 + 최근 뉴스를
                참고해 현재 장세 판단 + 단기 watchlist를 냅니다. 첫 틱 후
                여기 표시됩니다.
              </p>
            </div>
            {runButton('지금 실행')}
          </div>
        </Card.Body>
      </Card>
    )
  }
  const regimeTone =
    latest.regime === 'risk_on'
      ? 'border-l-emerald-500'
      : latest.regime === 'risk_off'
      ? 'border-l-red-500'
      : latest.regime === 'degraded'
      ? 'border-l-amber-500'
      : 'border-l-border'
  return (
    <Card className={`border-l-4 ${regimeTone}`}>
      <Card.Body>
        <div className="mb-2 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-foreground">
              실시간 증시 분석 · KIS Intraday
            </h2>
            <p className="text-xs text-muted-foreground">
              Nightly 대비 장 중 교차검증 · 30분 주기 · {items.length}건 이력
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant={
                latest.regime === 'risk_on'
                  ? 'success'
                  : latest.regime === 'risk_off'
                  ? 'destructive'
                  : latest.regime === 'degraded'
                  ? 'warning'
                  : 'muted'
              }
              shape="pill"
              title={latest.regime}
            >
              {latest.regime === 'risk_on'
                ? '위험 선호'
                : latest.regime === 'risk_off'
                ? '위험 회피'
                : latest.regime === 'degraded'
                ? '분석 실패'
                : '중립'}
            </Badge>
            {runButton('지금 실행')}
          </div>
        </div>
        {runIntraday.isSuccess && (
          <p className="mb-2 text-[11px] text-muted-foreground">
            ✓ 실행 중 — 15초 후 자동 새로고침. 진행 상황은 Jobs 메뉴에서 확인.
          </p>
        )}

        {latest.regime === 'degraded' ? (
          <div className="rounded-md border border-warning-soft bg-warning-soft p-2 text-xs text-warning-soft-foreground">
            <p className="font-medium">최근 틱에서 LLM 호출 실패</p>
            <p className="mt-1 font-mono break-all">{latest.reason}</p>
          </div>
        ) : (
          <>
            {latest.macro_summary && (
              <p className="mb-2 text-sm text-foreground whitespace-pre-wrap">
                {latest.macro_summary}
              </p>
            )}
            {latest.risk_notes && (
              <div className="mb-2 rounded border-l-2 border-warning bg-warning-soft p-2 text-xs text-warning-soft-foreground">
                <span className="font-medium">단기 리스크: </span>
                {latest.risk_notes}
              </div>
            )}
            {(latest.watchlist?.length ?? 0) > 0 && (
              <ul className="mb-2 grid grid-cols-1 gap-1.5 md:grid-cols-3">
                {latest.watchlist.map((t) => (
                  <li
                    key={t.symbol}
                    className="rounded-md border border-border bg-card p-2 text-xs"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-sm">{t.symbol}</span>
                      {krxName(t.symbol) && (
                        <span className="text-xs text-muted-foreground">
                          {krxName(t.symbol)}
                        </span>
                      )}
                      <Badge
                        variant={t.side === 'long' ? 'success' : 'destructive'}
                        shape="pill"
                        title={t.side}
                      >
                        {t.side === 'long' ? '매수' : '매도'}
                      </Badge>
                      <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                        c{t.conviction}
                      </span>
                    </div>
                    {t.thesis && (
                      <p className="mt-1 text-muted-foreground line-clamp-2">
                        {t.thesis}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {(latest.source_refs?.length ?? 0) > 0 && (
              <div className="mb-2 text-[11px] text-muted-foreground">
                <span className="font-medium">근거 데이터: </span>
                {latest.source_refs!.join(' · ')}
              </div>
            )}
          </>
        )}

        {/* Regime transition strip — scan the prior ticks to catch
            the moment the model flipped risk_on→off etc. */}
        {items.length > 1 && (
          <div className="mt-2 border-t border-border pt-2">
            <p className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
              최근 이력
            </p>
            <ul className="flex flex-wrap gap-1.5 text-[11px]">
              {items.slice(0, 12).map((s) => {
                const tone =
                  s.regime === 'risk_on'
                    ? 'text-emerald-500'
                    : s.regime === 'risk_off'
                    ? 'text-red-500'
                    : s.regime === 'degraded'
                    ? 'text-amber-500'
                    : 'text-muted-foreground'
                // Korean label matches the main regime badge above —
                // 위험 선호 / 위험 회피 / 분석 실패 / 중립. The raw
                // English enum stays in `title` for debugging.
                const label =
                  s.regime === 'risk_on'
                    ? '위험 선호'
                    : s.regime === 'risk_off'
                    ? '위험 회피'
                    : s.regime === 'degraded'
                    ? '분석 실패'
                    : '중립'
                return (
                  <li
                    key={s.id}
                    className="flex items-baseline gap-1 rounded border border-border bg-card px-1.5 py-0.5"
                    title={`${s.regime} · ${s.macro_summary || s.reason || ''}`.trim()}
                  >
                    <span className="font-mono text-muted-foreground">
                      {formatTimeShortKST(s.ticked_at)}
                    </span>
                    <span className={tone}>{label}</span>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// SuggestionsTab values — '' (전체), 'KR', 'US'. The empty string is
// the pseudo-value the URLSearchParams logic in api.suggestions strips
// out, giving us "all markets" without an explicit query param.
type SuggestionsTab = '' | 'KR' | 'US'

function SuggestionsSection() {
  const qc = useQueryClient()
  const [tab, setTab] = useState<SuggestionsTab>('')

  const q = useQuery<SuggestionsListResponse>({
    queryKey: ['suggestions', 'active', tab],
    queryFn: () => api.suggestions('active', tab || undefined),
    refetchInterval: 2 * 60_000,
    refetchOnWindowFocus: false,
  })
  const gen = useMutation({
    // Generate-now respects the active tab: '' (전체) → run both markets,
    // 'KR' / 'US' → scope to that market only. The dispatcher then
    // honours per-market kill switches in settings.
    mutationFn: () => api.suggestionsGenerate(tab ? tab : 'both'),
    onSuccess: (res) => {
      // Update every market-scope cache so a tab switch right after
      // generate doesn't show a stale loading state. Splits the items
      // by market for the KR / US tabs, keeps the union for ''.
      qc.setQueryData<SuggestionsListResponse>(
        ['suggestions', 'active', ''],
        { items: res.items },
      )
      qc.setQueryData<SuggestionsListResponse>(
        ['suggestions', 'active', 'KR'],
        { items: res.items.filter((it) => (it.market ?? '') !== 'US') },
      )
      qc.setQueryData<SuggestionsListResponse>(
        ['suggestions', 'active', 'US'],
        { items: res.items.filter((it) => it.market === 'US') },
      )
    },
  })
  const review = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'accepted' | 'rejected' }) =>
      api.suggestionReview(id, status),
    onSuccess: (_res, vars) => {
      // Invalidate every market-scoped cache — accept/reject changes
      // status (no longer 'active') so all tabs need a refresh.
      qc.invalidateQueries({ queryKey: ['suggestions'] })
      // Accept hook: server auto-adds the ticker to watchlist. Refresh
      // the Watched card so the new chip appears without a page reload.
      if (vars.status === 'accepted') {
        qc.invalidateQueries({ queryKey: ['watchlist'] })
      }
      // Clear the previous Generate diagnostic banner — operator just
      // engaged with the list (accepted/rejected), so "LLM이 추천을
      // 생성했지만 모든 티커 형식이 잘못되었습니다" from a stale run
      // is no longer informative and reads as a stuck error.
      gen.reset()
    },
  })

  const items = q.data?.items ?? []
  // Hide the diagnostic banner once there's a persisted list to
  // interact with — the last Generate's counters are only useful as
  // a "why is nothing here?" explainer; once items exist, the banner
  // contradicts the obvious UI signal and confuses operators.
  const lastRun: SuggestionsGenerateResponse | undefined =
    items.length > 0 ? undefined : gen.data

  // Generate button label reflects the active tab so the operator
  // knows the click won't accidentally fire both markets.
  const genLabel = gen.isPending
    ? '생성 중…'
    : tab === 'KR'
      ? 'KR 새로 추천'
      : tab === 'US'
        ? 'US 새로 추천'
        : '새로 추천 (전체)'

  return (
    <Card>
      <Card.Body>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="text-sm font-semibold text-muted-foreground">
              Suggestions · 종목 추천
            </h2>
            <p className="text-xs text-muted-foreground">
              최근 뉴스 + 보유 포지션 기반 LLM 제안. KR (KOSPI/KOSDAQ) ·
              US (NASDAQ/NYSE/AMEX). 자동 매매 아님 — 검토 후 직접 실행.
            </p>
          </div>
          <button
            type="button"
            className="rounded-md border border-border bg-card px-3 py-1 text-xs font-medium text-foreground hover:bg-muted/50 disabled:opacity-50"
            onClick={() => gen.mutate()}
            disabled={gen.isPending}
            title={`현재 ${tab || '전체'} 시장에 대해 추천 재생성`}
          >
            {genLabel}
          </button>
        </div>

        {/* Tab strip — semantic tokens only, dark-mode safe. */}
        <SuggestionsTabStrip
          active={tab}
          onChange={setTab}
          counts={{
            all: items.length,
            // Per-market counts populate from the cached response;
            // when on a non-empty tab we still want to show "n" for
            // the OTHER tabs so empty states are obvious.
          }}
        />

        {gen.isError && (
          <p className="mb-2 text-xs text-destructive">
            생성 실패: {String(gen.error)}
          </p>
        )}

        {/* Diagnostic banner — surfaces "LLM emitted N rows but X were
            dropped" so the operator can distinguish a real "no
            recommendation today" from a parser/validator drop. Only
            renders after a generate run, and only when something
            interesting happened (drops or a mismatch with persisted). */}
        {lastRun && (lastRun.llm_row_count > 0 || items.length > 0) && (
          <SuggestionsDiagnosticsBanner stats={lastRun} persisted={items.length} />
        )}

        {q.isPending && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
        {q.data && items.length === 0 && !gen.isPending && !lastRun && (
          <p className="text-sm text-muted-foreground">
            {tab === 'US'
              ? 'US 시장 활성 추천이 없습니다. "US 새로 추천" 버튼을 누르세요.'
              : tab === 'KR'
                ? 'KR 시장 활성 추천이 없습니다. "KR 새로 추천" 버튼을 누르세요.'
                : '현재 활성 추천이 없습니다. "새로 추천" 버튼으로 생성하세요.'}
          </p>
        )}

        {items.length > 0 && (
          <ul className="space-y-2">
            {items.map((it) => (
              <li key={it.id}>
                <SuggestionCard
                  item={it}
                  onAccept={() => review.mutate({ id: it.id, status: 'accepted' })}
                  onReject={() => review.mutate({ id: it.id, status: 'rejected' })}
                  reviewing={review.isPending}
                />
              </li>
            ))}
          </ul>
        )}
      </Card.Body>
    </Card>
  )
}

// SuggestionsTabStrip is the KR / US / 전체 selector. Compact 3-button
// row using semantic tokens — selected tab uses bg-primary, idle tabs
// border + bg-card. Mirrors the cluster admin's filter bar.
function SuggestionsTabStrip({
  active,
  onChange,
}: {
  active: SuggestionsTab
  onChange: (next: SuggestionsTab) => void
  counts?: { all?: number }
}) {
  const tabs: { key: SuggestionsTab; label: string; tone: string }[] = [
    { key: '', label: '전체', tone: '' },
    { key: 'KR', label: 'KR', tone: 'border-info-soft' },
    { key: 'US', label: 'US', tone: 'border-success-soft' },
  ]
  return (
    <div className="mb-3 flex gap-1 text-xs">
      {tabs.map((t) => {
        const selected = t.key === active
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={
              selected
                ? 'rounded-md bg-primary px-3 py-1 font-medium text-primary-foreground'
                : `rounded-md border bg-card px-3 py-1 text-foreground hover:bg-muted/50 ${t.tone || 'border-border'}`
            }
          >
            {t.label}
          </button>
        )
      })}
    </div>
  )
}

function SuggestionCard({
  item,
  onAccept,
  onReject,
  reviewing,
}: {
  item: StockSuggestion
  onAccept: () => void
  onReject: () => void
  reviewing: boolean
}) {
  const actionTone: Record<StockSuggestion['action'], string> = {
    buy:   'border-l-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/10',
    sell:  'border-l-red-500 bg-red-50/50 dark:bg-red-900/10',
    watch: 'border-l-blue-500 bg-blue-50/50 dark:bg-blue-900/10',
    avoid: 'border-l-amber-500 bg-amber-50/50 dark:bg-amber-900/10',
  }
  const actionLabel: Record<StockSuggestion['action'], string> = {
    buy:   '매수',
    sell:  '매도',
    watch: '관찰',
    avoid: '회피',
  }
  const actionVariant: Record<StockSuggestion['action'], 'success' | 'destructive' | 'info' | 'warning'> = {
    buy:   'success',
    sell:  'destructive',
    watch: 'info',
    avoid: 'warning',
  }
  // Market pill — semantic info/success tones. Empty market (legacy
  // pre-migration rows) treated as KR.
  const marketLabel = item.market === 'US' ? 'US' : 'KR'
  const marketVariant: 'info' | 'success' = item.market === 'US' ? 'success' : 'info'
  // KR rows resolve a 한글 종목명 from the static map; US rows get the
  // raw ticker only — the watchlist accept-hook stores the official
  // company name so the post-accept Watched chip carries it.
  const displayName = item.market === 'US' ? '' : krxName(item.ticker)
  return (
    <div className={`rounded-md border p-3 border-l-4 ${actionTone[item.action]}`}>
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm font-semibold">{item.ticker}</span>
          {displayName && (
            <span className="text-sm text-foreground">{displayName}</span>
          )}
          <Badge variant={marketVariant} shape="pill">
            {marketLabel}
          </Badge>
          <Badge variant={actionVariant[item.action]} shape="pill">
            {actionLabel[item.action]}
          </Badge>
          <span className="text-xs text-muted-foreground">
            conviction {item.conviction.toFixed(2)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="rounded-md border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 dark:bg-emerald-900/20 dark:border-emerald-800"
            onClick={onAccept}
            disabled={reviewing}
          >
            수락
          </button>
          <button
            type="button"
            className="rounded-md border border-red-300 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50 dark:bg-red-900/20 dark:border-red-800"
            onClick={onReject}
            disabled={reviewing}
          >
            거절
          </button>
        </div>
      </div>
      <p className="text-sm leading-relaxed mb-2">{item.rationale}</p>
      {item.evidence.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {item.evidence.map((ev, i) => (
            <span
              key={i}
              className="rounded-full bg-white/60 dark:bg-black/30 border border-border px-2 py-0.5 text-[11px]"
              title={ev.note ?? ''}
            >
              <span className="font-mono">{ev.kind}</span>
              {ev.id && <span className="ml-1 text-muted-foreground">{ev.id}</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

// BithumbSection — crypto provider dashboard card. Hidden entirely when
// the credentials row is absent: "Bithumb 설정이 없습니다" would be noise
// for operators who only run KIS. When configured, shows KRW cash + per-
// coin positions priced at the current Bithumb ticker.
function BithumbSection() {
  const q = useQuery<DashboardBithumb>({
    queryKey: ['dashboard', 'bithumb'],
    queryFn: () => api.dashboardBithumb(),
    refetchInterval: 30_000,
    retry: false,
  })
  if (!q.data || !q.data.configured) return null

  const data = q.data
  const krw = new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 })
  const coin = new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 8 })
  const fmtKRW = (s: string) => {
    const n = Number(s)
    if (!Number.isFinite(n)) return s
    return krw.format(n)
  }
  const fmtCoin = (s: string) => {
    const n = Number(s)
    if (!Number.isFinite(n)) return s
    return coin.format(n)
  }

  return (
    <Card>
      <Card.Header className="flex items-center justify-between gap-3">
        <div>
          <Card.Title className="flex items-center gap-2">
            Bithumb
            <Badge variant="warning">LIVE</Badge>
          </Card.Title>
          <Card.Description>
            실계좌 — 24/7 거래. 단계 A: 조회 전용.
          </Card.Description>
        </div>
        {data.fetched_at > 0 && (
          <span className="text-xs text-muted-foreground">
            fetched {formatTimeShortKST(data.fetched_at)}
          </span>
        )}
      </Card.Header>
      <Card.Body>
        {data.error && (
          <div className="mb-2 rounded-md border border-warning-soft bg-warning-soft px-3 py-2 text-xs text-warning-soft-foreground">
            {data.error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 mb-3">
          <StatCard label="현금 KRW" value={fmtKRW(data.cash_krw) + ' 원'} />
          <StatCard label="평가 자산 KRW" value={fmtKRW(data.equity_krw) + ' 원'} />
          <StatCard label="보유 종목" value={String(data.positions.length)} />
        </div>

        {data.positions.length === 0 ? (
          <p className="text-sm text-muted-foreground">보유 코인이 없습니다.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr>
                  <th className="py-1 text-left">심볼</th>
                  <th className="py-1 text-right">보유</th>
                  <th className="py-1 text-right">평단 (KRW)</th>
                  <th className="py-1 text-right">현재가 (KRW)</th>
                  <th className="py-1 text-right">전일대비</th>
                  <th className="py-1 text-right">평가액 (KRW)</th>
                  <th className="py-1 text-right">평가손익 (KRW)</th>
                </tr>
              </thead>
              <tbody>
                {data.positions.map((p) => {
                  const pl = Number(p.unrealized_pl)
                  const plClass = !Number.isFinite(pl)
                    ? ''
                    : pl > 0
                    ? 'text-emerald-500'
                    : pl < 0
                    ? 'text-red-500'
                    : ''
                  const change = p.change_pct_24h ?? 0
                  const changeTone =
                    change > 0
                      ? 'text-emerald-500'
                      : change < 0
                      ? 'text-red-500'
                      : 'text-muted-foreground'
                  return (
                    <tr key={p.symbol} className="border-t border-border">
                      <td className="py-1">
                        <span className="font-mono">{p.symbol}</span>
                        {symbolName(p.symbol) && (
                          <span className="ml-1.5 text-xs text-muted-foreground">
                            {symbolName(p.symbol)}
                          </span>
                        )}
                      </td>
                      <td className="py-1 text-right font-mono">{fmtCoin(p.quantity)}</td>
                      <td className="py-1 text-right font-mono">{fmtKRW(p.avg_buy_price)}</td>
                      <td className="py-1 text-right font-mono">{fmtKRW(p.mark_price)}</td>
                      <td
                        className={`py-1 text-right font-mono ${changeTone}`}
                        title={
                          p.prev_closing_krw
                            ? `전일 종가 ${fmtKRW(p.prev_closing_krw)}`
                            : '전일 종가 없음'
                        }
                      >
                        {p.prev_closing_krw
                          ? `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`
                          : '—'}
                      </td>
                      <td className="py-1 text-right font-mono">{fmtKRW(p.notional_krw)}</td>
                      <td className={`py-1 text-right font-mono ${plClass}`}>
                        {fmtKRW(p.unrealized_pl)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Shadow-session verdicts — the read-only crypto section's
            primary analytic output. Order placement + history were
            previously rendered here; operator moved those to a
            dedicated crypto trading surface so the Dashboard crypto
            card stays read-only ("at-a-glance status"), not
            "operate the broker". */}
        <div className="mt-4 space-y-3 border-t border-border pt-4">
          <CryptoVerdictsPanel />
        </div>
      </Card.Body>
    </Card>
  )
}

// CryptoVerdictsPanel renders the shadow-session journal. The raw
// stream was 50 rows deep (typically 4 symbols × latest ~12 ticks)
// which read as a firehose even after the state-change write gate
// landed — the dashboard wants CURRENT STANCE, not history. Layout:
//   1. 심볼별 현재 판정 — one compact card per symbol showing the
//      latest verdict + score + conviction + how long that stance
//      has held. Answers "what does the model think right now?".
//   2. 최근 변화 — most recent 5 transitions (verdict text
//      different from the row immediately before it per symbol).
//      Answers "what just changed?". Full history lives on the
//      /plan page's crypto section for the deeper view.
function CryptoVerdictsPanel() {
  const q = useQuery<{ items: CryptoVerdict[] }>({
    queryKey: ['crypto', 'verdicts'],
    queryFn: () => api.cryptoVerdicts(undefined, 50),
    refetchInterval: 30_000,
  })
  const items = q.data?.items ?? []

  // Latest per symbol. Items come back newest-first so the first
  // time we see a symbol is the current stance.
  const latestBySymbol = new Map<string, CryptoVerdict>()
  for (const v of items) {
    if (!latestBySymbol.has(v.symbol)) latestBySymbol.set(v.symbol, v)
  }
  const latest = Array.from(latestBySymbol.values()).sort(
    (a, b) => b.ticked_at - a.ticked_at,
  )

  // Recent transitions: walk per-symbol history in chronological
  // order and keep rows where verdict text flipped. Capped at 5 so
  // the card stays scannable; Plan page keeps the fuller audit view.
  const bySymbolChrono = new Map<string, CryptoVerdict[]>()
  for (const v of items) {
    const arr = bySymbolChrono.get(v.symbol) ?? []
    arr.push(v)
    bySymbolChrono.set(v.symbol, arr)
  }
  const transitions: CryptoVerdict[] = []
  for (const arr of bySymbolChrono.values()) {
    // items arrive newest-first; reverse for chronological walk.
    const chrono = [...arr].reverse()
    for (let i = 1; i < chrono.length; i++) {
      if (chrono[i].verdict !== chrono[i - 1].verdict) {
        transitions.push(chrono[i])
      }
    }
  }
  transitions.sort((a, b) => b.ticked_at - a.ticked_at)
  const recentTransitions = transitions.slice(0, 5)

  return (
    <div className="space-y-3">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
        24/7 섀도 세션 판정
        <Badge variant="muted" shape="pill">shadow-only</Badge>
      </h3>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          아직 판정 기록이 없습니다. 세션이 5분마다 작동하며, 첫 틱 후
          여기 나타납니다. 워치 심볼/주기는 Settings → Crypto Session.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {latest.map((v) => (
              <CryptoVerdictCell key={v.symbol} v={v} />
            ))}
          </div>
          {recentTransitions.length > 0 && (
            <div>
              <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                최근 변화
              </h4>
              <ul className="space-y-1 text-xs">
                {recentTransitions.map((v) => (
                  <li
                    key={v.id}
                    className="flex items-center gap-2 border-t border-border pt-1"
                  >
                    <span className="whitespace-nowrap text-muted-foreground">
                      {formatTimeShortKST(v.ticked_at)}
                    </span>
                    <span className="font-mono">{v.symbol}</span>
                    {symbolName(v.symbol) && (
                      <span className="text-xs text-muted-foreground">
                        {symbolName(v.symbol)}
                      </span>
                    )}
                    <CryptoVerdictBadge verdict={v.verdict} />
                    <span
                      className={`font-mono tabular-nums ${
                        v.score > 0.2
                          ? 'text-emerald-500'
                          : v.score < -0.2
                          ? 'text-red-500'
                          : ''
                      }`}
                    >
                      {v.score >= 0 ? '+' : ''}
                      {v.score.toFixed(2)}
                    </span>
                    <span className="truncate text-muted-foreground" title={v.reason}>
                      {v.reason}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// CryptoVerdictCell is the per-symbol card in the dashboard panel.
// Answers "what does the shadow session think RIGHT NOW about this
// coin?" at a glance: latest verdict badge + score + last tick time.
// Conviction + reason tuck into the title attribute for hover so the
// grid stays compact.
function CryptoVerdictCell({ v }: { v: CryptoVerdict }) {
  const scoreTone =
    v.score > 0.2
      ? 'text-emerald-500'
      : v.score < -0.2
      ? 'text-red-500'
      : 'text-foreground'
  return (
    <div
      className="flex flex-col gap-1 rounded-md border border-border bg-card p-2"
      title={`conviction ${v.conviction.toFixed(2)} · ${v.reason}`}
    >
      <div className="flex items-baseline justify-between gap-1">
        <span className="font-mono text-[11px] text-muted-foreground">
          {v.symbol}
        </span>
        <span className="text-[10px] text-muted-foreground">
          {formatTimeShortKST(v.ticked_at)}
        </span>
      </div>
      <div className="flex items-center justify-between gap-1">
        <CryptoVerdictBadge verdict={v.verdict} />
        <span className={`font-mono text-sm tabular-nums ${scoreTone}`}>
          {v.score >= 0 ? '+' : ''}
          {v.score.toFixed(2)}
        </span>
      </div>
    </div>
  )
}

function CryptoVerdictBadge({ verdict }: { verdict: CryptoVerdict['verdict'] }) {
  const map: Record<
    CryptoVerdict['verdict'],
    { label: string; variant: 'success' | 'destructive' | 'muted' | 'warning' }
  > = {
    buy: { label: '매수', variant: 'success' },
    sell: { label: '매도', variant: 'destructive' },
    hold: { label: '관망', variant: 'muted' },
    skip: { label: '무시', variant: 'warning' },
  }
  const e = map[verdict] ?? { label: verdict, variant: 'muted' as const }
  return (
    <Badge variant={e.variant} shape="pill">
      {e.label}
    </Badge>
  )
}

// CryptoVerdictsOverviewTab is the "가격 신호" tab body inside the
// crypto FlowCard. Source: 24/7 shadow-session verdicts — BTC
// momentum + breadth + volume signal, NOT news sentiment. Operator
// reads this alongside the 뉴스 감성 tab to spot divergences (뉴스
// bullish vs 가격 sell → leading indicator for mean reversion).
//
// Rendered as a tab body (no outer Card) because the FlowCard already
// provides the chrome. Previously lived in its own card under the
// Bithumb provider section; merged here per operator request so the
// two "market feel" views live side by side.
function CryptoVerdictsOverviewTab() {
  const q = useQuery<{ items: CryptoVerdict[] }>({
    queryKey: ['crypto', 'verdicts', 'overview'],
    queryFn: () => api.cryptoVerdicts(undefined, 50),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  })
  const items = q.data?.items ?? []
  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        섀도 세션 판정 없음. 판정이 쌓이면 여기 표시됩니다.
      </p>
    )
  }

  // Dedupe to the latest verdict per symbol — a 50-row slice will
  // usually repeat the same symbols across ticks; we want the fresh
  // one only so the distribution isn't biased by tick count.
  const bySymbol = new Map<string, CryptoVerdict>()
  for (const v of items) {
    const prev = bySymbol.get(v.symbol)
    if (!prev || v.ticked_at > prev.ticked_at) bySymbol.set(v.symbol, v)
  }
  const latest = Array.from(bySymbol.values())
  let buy = 0, sell = 0, hold = 0, skip = 0
  let sum = 0
  for (const v of latest) {
    sum += v.score
    switch (v.verdict) {
      case 'buy':  buy++; break
      case 'sell': sell++; break
      case 'hold': hold++; break
      case 'skip': skip++; break
    }
  }
  const avg = latest.length ? sum / latest.length : 0
  const sentiment: NewsSentiment =
    avg >= 0.2 ? 'bullish'
    : avg <= -0.2 ? 'bearish'
    : buy > 0 && sell > 0 ? 'mixed'
    : 'neutral'

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          24/7 섀도 세션 판정 · 심볼 {latest.length}개 · BTC 모멘텀 +
          breadth + volume 시그널 기반 (뉴스 감성과 독립)
        </p>
        <MarketMoodBadge sentiment={sentiment} score={avg} />
      </div>
      <MarketSentimentChart
        bullish={buy}
        bearish={sell}
        neutral={hold}
        mixed={skip}
        score={avg}
      />
      <ul className="grid grid-cols-1 gap-1.5 md:grid-cols-2 auto-rows-fr">
        {latest
          .sort((a, b) => Math.abs(b.score) - Math.abs(a.score))
          .slice(0, 6)
          .map((v) => (
            <li
              key={v.symbol}
              className="rounded-md border border-l-4 border-border bg-card px-2 py-1.5"
              style={{
                borderLeftColor:
                  v.verdict === 'buy' ? '#34d399'
                  : v.verdict === 'sell' ? '#f87171'
                  : v.verdict === 'skip' ? '#fbbf24'
                  : undefined,
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-baseline gap-1.5">
                  <span className="font-mono text-sm">{v.symbol}</span>
                  {symbolName(v.symbol) && (
                    <span className="text-xs text-muted-foreground">
                      {symbolName(v.symbol)}
                    </span>
                  )}
                </span>
                <span
                  className={`font-mono text-xs ${
                    v.score > 0.2 ? 'text-emerald-500'
                    : v.score < -0.2 ? 'text-red-500'
                    : 'text-muted-foreground'
                  }`}
                >
                  {v.score >= 0 ? '+' : ''}
                  {v.score.toFixed(2)}
                </span>
              </div>
              <p className="mt-0.5 line-clamp-2 text-[11px] text-muted-foreground">
                {v.reason}
              </p>
            </li>
          ))}
      </ul>
    </div>
  )
}

// (KISMarketOverviewSection removed — KISFlowCard's "흐름" tab now
// hosts the sentiment graph + distribution chart, with detailed news
// rows moved into the "뉴스" tab. The MarketOverviewRow + MarketMood-
// Badge widgets stay because CryptoMarketOverviewSection still uses
// them.)

// MarketSentimentChart is a two-part visual: (top) a horizontal
// stacked bar showing the bullish/bearish/neutral/mixed distribution
// as a percentage of the aggregate, and (bottom) a score gauge that
// plots the mean sentiment score on a [-1, +1] spectrum with 0 as
// the centre. The operator reads mood at a glance without doing
// mental math on the raw counters.
function MarketSentimentChart({
  bullish,
  bearish,
  neutral,
  mixed,
  score,
}: {
  bullish: number
  bearish: number
  neutral: number
  mixed: number
  score: number
}) {
  const total = bullish + bearish + neutral + mixed
  if (total === 0) return null
  const pct = (n: number) => (n / total) * 100
  // Score gauge: clamp + map [-1,+1] → [0%,100%] with 0 → 50%.
  const clamped = Math.max(-1, Math.min(1, score))
  const markerLeft = ((clamped + 1) / 2) * 100

  return (
    <div className="mb-1 space-y-2">
      {/* Distribution bar */}
      <div>
        <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
          <span>감성 분포</span>
          <span>
            <span className="text-emerald-500">강세 {bullish}</span> ·{' '}
            <span className="text-red-500">약세 {bearish}</span> ·{' '}
            <span className="opacity-60">중립 {neutral}</span> ·{' '}
            <span className="text-amber-500">혼조 {mixed}</span>
          </span>
        </div>
        {/* Neutral segment uses very low opacity so it reads as
            "empty space" — the eye tracks the bullish (emerald) /
            bearish (red) / mixed (amber) signal segments. A solid
            grey neutral fought the signal for attention. */}
        <div className="flex h-3 overflow-hidden rounded-full border border-border bg-card">
          <div
            className="h-full bg-emerald-500"
            style={{ width: `${pct(bullish)}%` }}
            title={`강세 ${bullish}건 (${pct(bullish).toFixed(1)}%)`}
          />
          <div
            className="h-full bg-amber-400"
            style={{ width: `${pct(mixed)}%` }}
            title={`혼조 ${mixed}건 (${pct(mixed).toFixed(1)}%)`}
          />
          <div
            className="h-full bg-muted/30"
            style={{ width: `${pct(neutral)}%` }}
            title={`중립 ${neutral}건 (${pct(neutral).toFixed(1)}%)`}
          />
          <div
            className="h-full bg-red-500"
            style={{ width: `${pct(bearish)}%` }}
            title={`약세 ${bearish}건 (${pct(bearish).toFixed(1)}%)`}
          />
        </div>
      </div>
      {/* Score gauge */}
      <div>
        <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
          <span>-1 약세</span>
          <span>평균 score {clamped >= 0 ? '+' : ''}{clamped.toFixed(2)}</span>
          <span>+1 강세</span>
        </div>
        {/* `via-transparent` drops the middle of the gauge to the
            card background so the stark grey band disappears; only
            the red ↔ emerald extremes remain visible, matching the
            actual signal (neutral region = no bias). */}
        <div className="relative h-2 w-full overflow-hidden rounded-full border border-border bg-card">
          <div className="absolute inset-0 bg-gradient-to-r from-red-500/80 via-transparent to-emerald-500/80" />
          {/* Centre tick (0). */}
          <div className="absolute top-0 left-1/2 h-full w-px -translate-x-1/2 bg-foreground/30" />
          {/* Score marker. */}
          <div
            className="absolute -top-0.5 h-3 w-1 -translate-x-1/2 rounded-sm bg-foreground shadow"
            style={{ left: `${markerLeft}%` }}
            title={`score ${clamped.toFixed(2)}`}
          />
        </div>
      </div>
    </div>
  )
}

function MarketMoodBadge({
  sentiment,
  score,
}: {
  sentiment: NewsSentiment
  score: number
}) {
  const label: Record<NewsSentiment, string> = {
    bullish: '강세',
    bearish: '약세',
    neutral: '중립',
    mixed: '혼조',
  }
  const tone: Record<NewsSentiment, string> = {
    bullish: 'text-emerald-500 border-emerald-500',
    bearish: 'text-red-500 border-red-500',
    neutral: 'text-muted-foreground border-border',
    mixed: 'text-amber-500 border-amber-500',
  }
  return (
    <div
      className={`inline-flex items-center gap-2 rounded-md border-2 px-3 py-1 text-sm font-semibold whitespace-nowrap ${tone[sentiment]}`}
    >
      {label[sentiment]}
      <span className="font-mono text-xs opacity-80">
        {score >= 0 ? '+' : ''}
        {score.toFixed(2)}
      </span>
    </div>
  )
}

// MarketOverviewRow removed — KISFlowCard's "뉴스" tab uses NewsSection's
// rich row renderer; the simpler 2-line clamped row is no longer needed.
